import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useLayoutEffect, useMemo, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { cls } from '@arco-design/mobile-utils';
import { getStyleWithVendor, useMountedState, useRefState } from '../../_helpers';
var PickerCell = /*#__PURE__*/forwardRef(function (props, ref) {
  var prefixCls = props.prefixCls,
      style = props.style,
      data = props.data,
      clickable = props.clickable,
      itemHeight = props.itemHeight,
      wrapperHeight = props.wrapperHeight,
      selectedValue = props.selectedValue,
      onValueChange = props.onValueChange,
      disabled = props.disabled,
      hideEmptyCols = props.hideEmptyCols,
      _props$rows = props.rows,
      rows = _props$rows === void 0 ? 5 : _props$rows,
      _props$touchToStop = props.touchToStop,
      touchToStop = _props$touchToStop === void 0 ? false : _props$touchToStop;

  var _useMountedState = useMountedState(''),
      transitionDuration = _useMountedState[0],
      setTransitionDuration = _useMountedState[1];

  var _useState = useState(''),
      bezier = _useState[0],
      setBezier = _useState[1];

  var _useMountedState2 = useMountedState(0),
      currentIndex = _useMountedState2[0],
      setCurrentIndex = _useMountedState2[1];

  var _useMountedState3 = useMountedState(selectedValue),
      currentValue = _useMountedState3[0],
      setCurrentValue = _useMountedState3[1]; // 是否开始touch
  // @en whether to start touch


  var _useRefState = useRefState(0),
      transformY = _useRefState[0],
      transformYRef = _useRefState[1],
      setTransformY = _useRefState[2];

  var lastTransformYRef = useRef(0);
  var touchStartTimeRef = useRef(0);
  var latestCallbackTimer = useRef(0);
  var touchStartXRef = useRef(0);
  var touchStartYRef = useRef(0);
  var touchingRef = useRef(false);
  var touchingXRef = useRef(null);
  var wrapRef = useRef(null);
  var movingStatusRef = useRef('normal');
  var rowCount = Math.max(rows % 2 === 0 ? rows + 1 : rows, 3);
  var isTouchMoveRef = useRef(false);
  var isTouchStopped = useRef(false);
  var timeRef = useRef(null);
  var colStyle = useMemo(function () {
    return getStyleWithVendor(_extends({
      transform: "translate3d(0px, " + (transformY || 0) + "px, 0px)"
    }, transitionDuration ? {
      transitionDuration: transitionDuration
    } : {}, {
      transitionTimingFunction: bezier,
      paddingBottom: (rowCount - 1) / 2 * itemHeight + "px",
      paddingTop: (rowCount - 1) / 2 * itemHeight + "px"
    }));
  }, [transitionDuration, transformY, bezier, itemHeight, rowCount]);

  function _scrollingComplete(nowItemIndex) {
    // index有改变时再抛出
    // @en Throws again when index changes
    if (currentIndex !== nowItemIndex) {
      setCurrentIndex(nowItemIndex);
      var newValue = data[nowItemIndex] && data[nowItemIndex].value;

      if (newValue !== currentValue) {
        // if (!('selectedValue' in props)) {
        //     setCurrentValue(newValue);
        // }
        setCurrentValue(newValue);

        if (onValueChange) {
          onValueChange(newValue);
        }
      }
    }
  }

  function _scrollTo(transY, transDuration, callback) {
    if (transDuration === void 0) {
      transDuration = 0;
    }

    if (callback === void 0) {
      callback = function callback() {};
    }

    setTransitionDuration(transDuration ? transDuration + "ms" : '');
    setTransformY(transY); // 处理连续滑动的情况：
    // @en handle the case of continuous sliding:
    // 如果上一次callback还未执行，先cancel掉上一次回调，只执行最近的一次回调
    // @en If the last callback has not been executed, cancel the last callback first, and only execute the latest callback

    if (latestCallbackTimer.current) {
      clearTimeout(latestCallbackTimer.current);
    }

    latestCallbackTimer.current = window.setTimeout(function () {
      movingStatusRef.current = 'normal';
      setTransitionDuration('');
      callback();
    }, transDuration);
  }

  function _scrollToIndex(itemIndex, transDuration, callback) {
    if (transDuration === void 0) {
      transDuration = 0;
    }

    if (callback === void 0) {
      callback = function callback() {};
    }

    _scrollTo(-1 * itemIndex * itemHeight, transDuration, callback);
  }

  function _scrollToIndexWithChange(itemIndex, transDuration) {
    if (transDuration === void 0) {
      transDuration = 0;
    }

    _scrollToIndex(itemIndex, transDuration, function () {
      _scrollingComplete(itemIndex);
    });
  }

  var _handleColumnTouchStart = useCallback(function (e) {
    if (disabled) {
      return;
    }

    isTouchMoveRef.current = false;
    movingStatusRef.current = 'moving';
    var x = e.touches[0].screenX;
    var y = e.touches[0].screenY;
    touchStartTimeRef.current = Date.now();
    touchingRef.current = true;
    touchingXRef.current = null;
    touchStartXRef.current = x;
    touchStartYRef.current = y;
    lastTransformYRef.current = transformYRef.current;
  }, [disabled]);

  var _handleColumnTouchMove = useCallback(function (e) {
    if (disabled || !touchingRef.current) {
      return;
    }

    isTouchMoveRef.current = true;
    var lastTransformY = lastTransformYRef.current;
    var touchMoveX = e.touches[0].screenX;
    var touchMoveY = e.touches[0].screenY;
    var distanceX = touchMoveX - touchStartXRef.current;
    var distance = touchMoveY - touchStartYRef.current;

    if (touchingXRef.current === null) {
      touchingXRef.current = Math.abs(distanceX) > Math.abs(distance);
    }

    if (touchingXRef.current) {
      return;
    }

    e.cancelable && e.preventDefault();
    var newPos = lastTransformY + distance;
    var maxPos = -1 * (data.length - 1) * itemHeight; // 当滚动到上边界或下边界时增加阻尼效果
    // @en Add damping effect when scrolling to the upper or lower border

    setTransformY(lastTransformY >= 0 && distance > 0 || lastTransformY <= maxPos && distance < 0 ? lastTransformY + distance / 4 : newPos);
  }, [data.length, disabled, itemHeight]);

  function _handleScrollEnd() {
    var maxIndex = data.length - 1;
    var nowIndex = Math.max(0, Math.min(maxIndex, Math.round(-1 * transformY / itemHeight))); // 滚动（包括加动量的滚动）完成之后定位到最近的一个index上
    // @en After scrolling (including scrolling with momentum) is completed, it is positioned on the nearest index

    _scrollToIndexWithChange(nowIndex, 200);
  } // Reference: https://juejin.im/post/6844904185121488910


  function momentum(current, start, duration, minY, maxY) {
    var durationMap = {
      noBounce: 2000,
      weekBounce: 500,
      strongBounce: 300
    };
    var bezierMap = {
      noBounce: 'cubic-bezier(.17, .89, .45, 1)',
      weekBounce: 'cubic-bezier(.25, .46, .45, .94)',
      strongBounce: 'cubic-bezier(.25, .46, .45, .94)'
    };
    var type = 'noBounce'; // 惯性滑动加速度
    // @en inertial sliding acceleration

    var deceleration = 0.003; // 回弹阻力
    // @en rebound resistance

    var bounceRate = 5; // 强弱回弹的分割值
    // @en Split value of strong and weak rebound

    var bounceThreshold = 300; // 回弹的最大限度
    // @en maximum rebound

    var maxOverflowY = wrapperHeight / 6;
    var overflowY;
    var distance = current - start;
    var speed = 2 * Math.abs(distance) / duration;
    var destination = current + speed / deceleration * (distance < 0 ? -1 : 1);

    if (destination < minY) {
      overflowY = minY - destination;
      type = overflowY > bounceThreshold ? 'strongBounce' : 'weekBounce';
      destination = Math.max(minY - maxOverflowY, minY - overflowY / bounceRate);
    } else if (destination > maxY) {
      overflowY = destination - maxY;
      type = overflowY > bounceThreshold ? 'strongBounce' : 'weekBounce';
      destination = Math.min(maxY + maxOverflowY, maxY + overflowY / bounceRate);
    }

    return {
      destination: destination,
      duration: durationMap[type],
      bezier: bezierMap[type]
    };
  }

  function _handleColumnTouchEnd() {
    _clearTimer();

    movingStatusRef.current = 'normal';
    var lastTransformY = lastTransformYRef.current;

    if (!isTouchMoveRef.current || transformY === lastTransformY) {
      return;
    }

    touchingRef.current = false;
    var endTime = Date.now();
    var scrollerHeight = (data.length + rowCount - 1) * itemHeight;
    var duration = endTime - touchStartTimeRef.current;
    var absDistY = Math.abs(transformY - lastTransformY); // 计算动量，保证滚动顺畅，条件：手势时间小于300ms && 移动距离绝对值大于30时
    // @en Calculate the momentum to ensure smooth scrolling, condition: the gesture time is less than 300ms && the absolute value of the moving distance is greater than 30

    if (duration < 300 && absDistY > 30) {
      var momentumY = momentum(transformY, lastTransformY, duration, wrapperHeight - scrollerHeight, 0);
      var newItemIndex = Math.max(0, Math.min(data.length - 1, Math.round(-1 * momentumY.destination / itemHeight)));
      setBezier(momentumY.bezier);
      movingStatusRef.current = 'scrolling';

      _scrollToIndex(newItemIndex, momentumY.duration, function () {
        _scrollingComplete(newItemIndex);
      });
    } else {
      _handleScrollEnd();
    }
  }

  function scrollToCurrentIndex() {
    if (!wrapRef.current) {
      return;
    }

    var curStyle = window.getComputedStyle(wrapRef.current);
    var transformMatrix = curStyle.transform || curStyle.webkitTransform || '';
    var transY = Number(transformMatrix.replace(/matrix\((.*?)\)/, '$1').split(/,\s*/).slice(-1)[0]) || 0;
    var maxIndex = data.length - 1;
    var nowIndex = Math.max(0, Math.min(maxIndex, Math.round(-1 * transY / itemHeight))); // 滚动（包括加动量的滚动）完成之后定位到最近的一个index上
    // @en After scrolling (including scrolling with momentum) is completed, it is positioned on the nearest index

    _scrollToIndexWithChange(nowIndex, 0);
  }

  function _clearTimer() {
    timeRef.current && clearTimeout(timeRef.current);
    timeRef.current = null;
  }

  function _handleItemTouchStart() {
    if (touchToStop === false) {
      return;
    }

    isTouchStopped.current = false;
    timeRef.current = window.setTimeout(function () {
      if (!isTouchMoveRef.current) {
        isTouchStopped.current = true;
        scrollToCurrentIndex();
      }
    }, touchToStop === true ? 100 : touchToStop);
  }

  function _handleItemClick(itemIndex) {
    if (!clickable || disabled || touchToStop !== false && isTouchStopped.current) {
      return;
    }

    _scrollToIndexWithChange(itemIndex, 200);
  }

  useEffect(function () {
    if (wrapRef.current) {
      wrapRef.current.addEventListener('touchstart', _handleColumnTouchStart);
      wrapRef.current.addEventListener('touchmove', _handleColumnTouchMove);
    }

    return function () {
      if (wrapRef.current) {
        wrapRef.current.removeEventListener('touchstart', _handleColumnTouchStart);
        wrapRef.current.removeEventListener('touchmove', _handleColumnTouchMove);
      }
    };
  }, [_handleColumnTouchStart, _handleColumnTouchMove]);
  useLayoutEffect(function () {
    if ('selectedValue' in props) {
      var curIndex = data.findIndex(function (item) {
        return item.value === selectedValue;
      });
      setCurrentIndex(curIndex);

      if (curIndex >= 0) {
        _scrollToIndexWithChange(curIndex);
      }
    }
  }, [selectedValue, itemHeight, data]);
  useImperativeHandle(ref, function () {
    return {
      movingStatus: movingStatusRef.current,
      scrollToCurrentIndex: scrollToCurrentIndex
    };
  });
  return !hideEmptyCols || data && data.length ? /*#__PURE__*/React.createElement("div", {
    className: prefixCls + "-column"
  }, /*#__PURE__*/React.createElement("div", {
    className: prefixCls + "-column-item-wrap",
    style: colStyle,
    ref: wrapRef,
    onTouchStart: _handleItemTouchStart,
    onTouchEnd: _handleColumnTouchEnd,
    onTouchCancel: _handleColumnTouchEnd
  }, data.map(function (item, index) {
    var _cls;

    var dis = Math.abs(index - currentIndex);
    return /*#__PURE__*/React.createElement("div", {
      key: index + "_" + item.value,
      className: cls(prefixCls + "-column-item", (_cls = {
        selected: index === currentIndex
      }, _cls["selected-neighbor-" + dis] = dis && dis <= Math.floor(rowCount / 2), _cls)),
      style: style,
      onClick: function onClick() {
        return _handleItemClick(index);
      }
    }, item.label);
  }))) : null;
});
export default PickerCell;